import {
  FilterState,
  Timeslot,
  spaceTypes,
  capacity,
  SortingTypeValue,
  VIEW_ALL_REGION,
  VIEW_ALL_BUILDING,
} from '@studio/atomic-ui'
import { addDays, addHours, addMinutes, format, getHours, getMinutes, isBefore, isSameDay, parse } from 'date-fns'
import { DateRange } from 'react-day-picker'
import { bookableHours, isProduction } from '../../env'
import { checkAvailability } from '../../services/bff/availability'
import { checkDayPassAvailability, prepareDateForBackend } from '../../templates/tm-pdp/booking-panel-config'
import { ProductType } from '../../types/product'
import { SortingParams } from '../../services/api/types'
import { SanitySmProduct } from '../../services/sanity/types'
import { FullProduct } from '.'

export const validateSpaceType = (type?: string): ProductType =>
  spaceTypes.find(({ typeKey }) => typeKey === type)?.typeKey ?? 'meeting_room'

export const getLabelFromSpaceType = (type?: string): string | undefined =>
  spaceTypes.find(({ typeKey }) => typeKey === type)?.label

export const emptyCapacity = { label: '', value: '' }

export const defaultFilterState = {
  loading: false,
  disabled: true,
  capacity: emptyCapacity,
  duration: { label: '', value: '' },
  location: {
    label: VIEW_ALL_REGION.label,
    value: VIEW_ALL_REGION.value,
  },
  building: {
    label: VIEW_ALL_BUILDING.label,
    value: VIEW_ALL_BUILDING.value,
  },
  date: { from: undefined, to: undefined },
  startTime: { label: '', value: '' },
  instantBook: false,
}

export const validPrice = (product: SanitySmProduct) => !!product[priceSanityField] && product[priceSanityField] > 0

export const preparePdpQueryParams = (state: FilterState) => {
  const search = new URLSearchParams()

  if (!state.date) return ''
  const { from } = state.date as DateRange
  if (!from) return ''

  const startTime: Timeslot | undefined = state.startTime?.value
    ? {
        label: '',
        timeslot: state.startTime.value,
      }
    : undefined

  const result = prepareDateForBackend(state.date as DateRange, startTime, state.duration)
  search.set('from', result.from)
  search.set('to', result.to)

  if (state.type === 'day_pass') search.set('quantity', '1')
  if (startTime) search.set('startTime', startTime.timeslot)
  if (state.duration?.value) {
    search.set('duration', state.duration.value)
    search.set('labelDuration', state.duration.label)
  }

  return search.toString()
}

export const isBookableWithinDate = async (
  product: FullProduct,
  state: FilterState,
  location: string
): Promise<{ isAvailable: boolean; inquiryOnly: boolean }> => {
  // inquire-only or unpriced products can't be booked, no need to query availability
  if (product.inquiryOnly || !validPrice(product)) {
    return { isAvailable: false, inquiryOnly: product.inquiryOnly }
  }

  // consider if range state is within bookable days, any date before the limit is valid for a given product
  const limit = addDays(Date.now(), product.bookableDays ?? 0)
  // dates selected must be before the limit set by the bookable days from today on
  const { from, to } = (state.date as DateRange | undefined) ?? {}
  const fromWithinLimit = from && isBefore(from, limit)
  const toWithinLimit = to && isBefore(to, limit)

  const dateFormat = (date: Date) => format(date, 'uuuu-MM-dd')

  const determineAvailability = (
    isAvailable: boolean,
    withinLimit: boolean | undefined
  ): { isAvailable: boolean; inquiryOnly: boolean } => {
    if (!isAvailable) {
      return {
        isAvailable: false,
        inquiryOnly: product.inquiryOnly,
      }
    }

    if (product.inquiryOnly) {
      return {
        isAvailable: true,
        inquiryOnly: true,
      }
    }

    if (withinLimit) {
      return {
        isAvailable: true,
        inquiryOnly: false,
      }
    }

    return {
      isAvailable: true,
      inquiryOnly: true,
    }
  }

  // Use product's own spaceType instead of filter's type
  // This ensures each product uses its own availability logic
  const productType = product.spaceType

  if (productType === 'day_pass') {
    const withinLimit = fromWithinLimit && (!to || (to && toWithinLimit))
    const response = await checkDayPassAvailability(location, product.source, state.date as DateRange | undefined)
    return determineAvailability(response >= 1, withinLimit)
  }

  if (state.date && 'from' in state.date && state.date.from) {
    if (state.date.to) {
      const startDate = `${dateFormat(state.date.from)} 00:00`
      const endDate = `${dateFormat(state.date.to)} 23:59`
      const { isAvailable } = await checkAvailability({
        startDate,
        endDate,
        bookingSource: product.source,
        productId: product.platformId,
        location,
        productType: product.spaceType,
      })

      return determineAvailability(isAvailable, toWithinLimit)
    }

    if (productType === 'office') {
      // Single value for office, prepare to value manually
      const { isAvailable } = await checkAvailability({
        startDate: `${dateFormat(state.date.from)} 00:00`,
        endDate: `${dateFormat(state.date.from)} 23:59`,
        bookingSource: product.source,
        productId: product.platformId,
        location,
        productType: product.spaceType,
      })
      return determineAvailability(isAvailable, fromWithinLimit)
    }

    if (state.duration?.value && state.startTime?.value && productType === 'meeting_room') {
      // Single value, prepare to value manually
      // first one with startTime (24 hr time), last one startTime+duration
      // use date-fns for it

      const day = format(state.date.from, 'uuuu-MM-dd')
      const from = `${day} ${state.startTime.value}`
      const startDate = parse(from, 'uuuu-MM-dd HH:mm', new Date())
      const [hours, minutes] = state.duration.value.split('.')
      const endDate =
        minutes === '5' ? addMinutes(addHours(startDate, parseInt(hours)), 30) : addHours(startDate, parseInt(hours))

      const { isAvailable } = await checkAvailability({
        startDate: `${dateFormat(state.date.from)} 00:00`,
        endDate: `${dateFormat(state.date.from)} 23:59`,
        bookingSource: product.source,
        productId: product.platformId,
        location,
        productType: product.spaceType,
        duration: state.duration?.value,
        startTime: state.startTime?.value,
      })

      const businessHours = product.spaceBusinessHours || product.buildingBusinessHours
      const sameDay = isSameDay(startDate, endDate)
      const endHour = getHours(endDate)
      const endMinutes = getMinutes(endDate)
      const maxBookableHour = businessHours
        ? parseInt(businessHours.endHour.split(':')[0])
        : bookableHours[product.spaceType].max
      const maxBookableMinutes = businessHours ? parseInt(businessHours.endHour.split(':')[1]) : 0
      const exceedsLimit = endHour > maxBookableHour || (endHour === maxBookableHour && endMinutes > maxBookableMinutes)
      return determineAvailability(isAvailable && sameDay && !exceedsLimit, fromWithinLimit)
    } else {
      const startDate = `${dateFormat(state.date.from)} 00:00`
      const endDate = `${dateFormat(state.date.from)} 23:59`
      const { isAvailable } = await checkAvailability({
        startDate,
        endDate,
        bookingSource: product.source,
        productId: product.platformId,
        location,
        productType: product.spaceType,
        duration: state.duration?.value,
        startTime: state.startTime?.value,
      })

      return determineAvailability(isAvailable, true)
    }
  }

  return determineAvailability(false, false)
}

/**
 * Retrieves the valid capacity value for a given type and current capacity.
 *
 * @param {string} type - The type of space for validating the capacity.
 * @param {string} currentCapacity - The current capacity being evaluated.
 * @returns {Object} - The capacity object that matches the validated value.
 *
 * */
export const getCapacityValue = (type: string, currentCapacity: string) => {
  let capacityValue = currentCapacity
  const lastElement = capacity[validateSpaceType(type)].at(-1)
  if (currentCapacity && lastElement && Number(capacityValue) > Number(lastElement.value)) {
    capacityValue = lastElement.value
  }
  return capacity[validateSpaceType(type)].find(({ value }) => capacityValue === value) || emptyCapacity
}

export const mapSortByType = (sortingType: SortingTypeValue): SortingParams => {
  if (sortingType === SortingTypeValue.PRICE_ASC) return { field: 'price', order: 'asc' }
  if (sortingType === SortingTypeValue.PRICE_DESC) return { field: 'price', order: 'desc' }
  if (sortingType === SortingTypeValue.CAPACITY_ASC) return { field: 'capacity', order: 'asc' }
  if (sortingType === SortingTypeValue.CAPACITY_DESC) return { field: 'capacity', order: 'desc' }

  return { field: 'price', order: 'asc' }
}

export const priceSanityField = isProduction ? 'prodStripePrice' : 'stripePrice'
